import { Component, signal } from '@angular/core';
import { form, required, minLength, Field, pattern } from '@angular/forms/signals';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { inject, OnInit } from '@angular/core';
import { SetPasswordService } from 'src/app/theme/shared/service/set-password.service';
import { ToastrService } from 'ngx-toastr';

interface SetPasswordData {
  newPassword: string;
  confirmPassword: string;
}

@Component({
  selector: 'app-auth-set-password',
  imports: [FormsModule, Field, CommonModule],
  templateUrl: './auth-set-password.html',
  styleUrl: './auth-set-password.scss'
})
export class AuthSetPassword implements OnInit {
  submitted = signal(false);
  loading = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private setPasswordService = inject(SetPasswordService);
  private toastr = inject(ToastrService);

  showPassword = false;
  showConfirmPassword = false;

  token = signal<string | null>(null);
  email = signal<string | null>(null);
  userType = signal<string | null>(null);

  ngOnInit() {
    this.route.queryParamMap.subscribe((params) => {
      this.token.set(params.get('token'));
      this.email.set(params.get('email'));
      this.userType.set(params.get('userType'));
    });
  }

  private readonly passwordModel = signal<SetPasswordData>({
    newPassword: '',
    confirmPassword: ''
  });

  passwordForm = form(this.passwordModel, (schemaPath) => {
    required(schemaPath.newPassword, {
      message: 'New password is required'
    });

    minLength(schemaPath.newPassword, 8, {
      message: 'Password must be at least 8 characters'
    });

    pattern(schemaPath.newPassword, /[A-Z]/, {
      message: 'Must contain at least one uppercase letter'
    });

    pattern(schemaPath.newPassword, /[a-z]/, {
      message: 'Must contain at least one lowercase letter'
    });

    pattern(schemaPath.newPassword, /[0-9]/, {
      message: 'Must contain at least one number'
    });

    pattern(schemaPath.newPassword, /[^A-Za-z0-9]/, {
      message: 'Must contain at least one special character'
    });

    required(schemaPath.confirmPassword, {
      message: 'Confirm password is required'
    });
  });

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  get passwordsDoNotMatch() {
    const value = this.passwordForm().value();
    return value.newPassword && value.confirmPassword && value.newPassword !== value.newPassword;
  }

  onSubmit() {
    if (this.loading()) return;

    this.submitted.set(true);

    const value = this.passwordForm().value();
    const token = this.token();

    this.errorMessage.set(null);
    this.successMessage.set(null);

    if (!token) {
      this.toastr.error('Invalid or missing token');
      return;
    }

    if (this.passwordForm().invalid()) {
      this.errorMessage.set('Please fix form errors.');
      return;
    }

    if (this.passwordsDoNotMatch) {
      this.errorMessage.set('Passwords do not match.');
      return;
    }

    this.loading.set(true);

    this.setPasswordService.sendResetEmail(token, value.newPassword, value.confirmPassword).subscribe({
      next: (response) => {
        this.loading.set(false);

        this.toastr.success(response?.message || 'Password updated successfully');

        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 1200);
      },
      error: (error) => {
        this.loading.set(false);

        this.toastr.error(error?.error?.message || 'Failed to update password');
      }
    });
  }
}
